import { DeleteIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Badge,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createScorecardConfigVersion,
  getLatestScorecardConfig,
  getScorecardConfigVersion,
  listScorecardConfigVersions,
} from 'api/tearsheetScorecardConfigApi';
import ContentBox from 'components/ContentBox';
import { useEffect, useState } from 'react';

const KIND_OPTIONS = [
  { value: 'numeric_higher_better', label: 'Numeric (higher better)' },
  { value: 'numeric_lower_better', label: 'Numeric (lower better)' },
  { value: 'qualitative', label: 'Qualitative' },
];

const DEFAULT_FINAL_SCORE_BANDS = {
  minAverageForGradeA: 2.5,
  minAverageForGradeB: 1.5,
};

const defaultGradeThresholdsDraft = () => ({
  exposureToRunRate: { grade3: 40, grade2: 20, grade1: 10 },
  exposureToRunOff: { grade3: 80, grade2: 50, grade1: 25 },
  finalScore: { ...DEFAULT_FINAL_SCORE_BANDS },
});

const emptyMetric = () => ({
  key: `metric_${Date.now()}`,
  label: '',
  kind: 'numeric_higher_better',
  unit: '',
  thresholdScore3: 0,
  thresholdScore2: 0,
  labelScore3: '',
  labelScore2: '',
  labelScore1: '',
});

function TearsheetScorecardConfigAdmin() {
  const toast = useToast();
  const saveModal = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('edit');
  const [viewDoc, setViewDoc] = useState(null);
  const [draftMetrics, setDraftMetrics] = useState([]);
  const [draftGradeThresholds, setDraftGradeThresholds] = useState(
    defaultGradeThresholdsDraft
  );
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  const bootstrap = async () => {
    setLoading(true);
    try {
      let lat = null;
      try {
        lat = await getLatestScorecardConfig();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listScorecardConfigVersions();
      setLatest(lat);
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftMetrics(JSON.parse(JSON.stringify(lat?.metrics || [])));
      setDraftGradeThresholds(() => {
        if (!lat?.gradeThresholds) {
          return defaultGradeThresholdsDraft();
        }
        const copy = JSON.parse(JSON.stringify(lat.gradeThresholds));
        if (!copy.finalScore) {
          copy.finalScore = { ...DEFAULT_FINAL_SCORE_BANDS };
        }
        return copy;
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
  }, []);

  const onVersionSelect = async (value) => {
    if (value === 'edit') {
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftMetrics(JSON.parse(JSON.stringify(latest?.metrics || [])));
      setDraftGradeThresholds(() => {
        if (!latest?.gradeThresholds) {
          return defaultGradeThresholdsDraft();
        }
        const copy = JSON.parse(JSON.stringify(latest.gradeThresholds));
        if (!copy.finalScore) {
          copy.finalScore = { ...DEFAULT_FINAL_SCORE_BANDS };
        }
        return copy;
      });
      return;
    }
    const v = Number(value);
    setSelectedVersion(v);
    try {
      const doc = await getScorecardConfigVersion(v);
      setViewDoc(doc);
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    }
  };

  const displayMetrics =
    selectedVersion === 'edit' ? draftMetrics : viewDoc?.metrics || [];
  const displayGradeThresholds =
    selectedVersion === 'edit'
      ? draftGradeThresholds
      : viewDoc?.gradeThresholds ?? defaultGradeThresholdsDraft();
  const displayFinalScoreBands =
    displayGradeThresholds.finalScore ?? DEFAULT_FINAL_SCORE_BANDS;
  const readOnly = selectedVersion !== 'edit';

  const updateGradeThreshold = (kpi, grade, rawValue) => {
    if (readOnly) return;
    const n = Number(rawValue);
    setDraftGradeThresholds((prev) => ({
      ...prev,
      [kpi]: { ...prev[kpi], [grade]: Number.isFinite(n) ? n : 0 },
    }));
  };

  const updateFinalScoreBand = (field, rawValue) => {
    if (readOnly) return;
    const n = Number(rawValue);
    setDraftGradeThresholds((prev) => ({
      ...prev,
      finalScore: {
        ...(prev.finalScore || DEFAULT_FINAL_SCORE_BANDS),
        [field]: Number.isFinite(n) ? n : 0,
      },
    }));
  };

  const updateMetric = (index, field, rawValue) => {
    if (readOnly) {
      return;
    }
    setDraftMetrics((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      if (field === 'thresholdScore3' || field === 'thresholdScore2') {
        const n = Number(rawValue);
        row[field] = Number.isFinite(n) ? n : 0;
      } else {
        row[field] = rawValue;
      }
      next[index] = row;
      return next;
    });
  };

  const deleteMetric = (index) => {
    if (readOnly) {
      return;
    }
    setDraftMetrics((prev) => prev.filter((_, i) => i !== index));
  };

  const addMetric = () => {
    if (readOnly) {
      return;
    }
    setDraftMetrics((prev) => [...prev, emptyMetric()]);
  };

  const confirmSave = async () => {
    const seen = new Set();
    for (const m of draftMetrics) {
      if (!String(m.label || '').trim()) {
        toast({
          title: 'Validation',
          description: 'Each metric needs a label',
          status: 'warning',
        });
        return;
      }
      if (!String(m.key || '').trim()) {
        toast({
          title: 'Validation',
          description: 'Each metric needs a key',
          status: 'warning',
        });
        return;
      }
      if (seen.has(m.key)) {
        toast({
          title: 'Validation',
          description: `Duplicate key: ${m.key}`,
          status: 'warning',
        });
        return;
      }
      seen.add(m.key);
    }
    setSaving(true);
    try {
      const minA = Number(draftGradeThresholds.finalScore?.minAverageForGradeA);
      const minB = Number(draftGradeThresholds.finalScore?.minAverageForGradeB);
      if (
        Number.isFinite(minA) &&
        Number.isFinite(minB) &&
        minA <= minB
      ) {
        toast({
          title: 'Validation',
          description:
            'Final score: minimum average for Grade A must be greater than minimum average for Grade B',
          status: 'warning',
        });
        return;
      }
      await createScorecardConfigVersion({
        metrics: draftMetrics.map((m) => ({
          ...m,
          thresholdScore3: Number(m.thresholdScore3) || 0,
          thresholdScore2: Number(m.thresholdScore2) || 0,
        })),
        gradeThresholds: {
          exposureToRunRate: {
            grade3: Number(draftGradeThresholds.exposureToRunRate?.grade3) || 0,
            grade2: Number(draftGradeThresholds.exposureToRunRate?.grade2) || 0,
            grade1: Number(draftGradeThresholds.exposureToRunRate?.grade1) || 0,
          },
          exposureToRunOff: {
            grade3: Number(draftGradeThresholds.exposureToRunOff?.grade3) || 0,
            grade2: Number(draftGradeThresholds.exposureToRunOff?.grade2) || 0,
            grade1: Number(draftGradeThresholds.exposureToRunOff?.grade1) || 0,
          },
          finalScore: {
            minAverageForGradeA:
              Number(draftGradeThresholds.finalScore?.minAverageForGradeA) ||
              DEFAULT_FINAL_SCORE_BANDS.minAverageForGradeA,
            minAverageForGradeB:
              Number(draftGradeThresholds.finalScore?.minAverageForGradeB) ||
              DEFAULT_FINAL_SCORE_BANDS.minAverageForGradeB,
          },
        },
        note: saveNote.trim() || undefined,
      });
      toast({
        title: 'Saved',
        description: 'New scorecard version created',
        status: 'success',
      });
      saveModal.onClose();
      setSaveNote('');
      let lat = null;
      try {
        lat = await getLatestScorecardConfig();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listScorecardConfigVersions();
      setLatest(lat);
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftMetrics(JSON.parse(JSON.stringify(lat?.metrics || [])));
      setDraftGradeThresholds(() => {
        if (!lat?.gradeThresholds) {
          return defaultGradeThresholdsDraft();
        }
        const copy = JSON.parse(JSON.stringify(lat.gradeThresholds));
        if (!copy.finalScore) {
          copy.finalScore = { ...DEFAULT_FINAL_SCORE_BANDS };
        }
        return copy;
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ContentBox>
      <HStack mb={6} justify="space-between" flexWrap="wrap" gap={3}>
        <HStack>
          <Heading size="md">Tearsheet scorecard config</Heading>
          {latest?.version != null ? (
            <Badge colorScheme="purple">v{latest.version}</Badge>
          ) : null}
        </HStack>
        <FormControl maxW="280px">
          <FormLabel fontSize="sm">View version</FormLabel>
          <Select
            value={selectedVersion === 'edit' ? 'edit' : String(selectedVersion)}
            onChange={(e) => onVersionSelect(e.target.value)}
          >
            <option value="edit">Latest (edit)</option>
            {(summaries || []).map((s) => (
              <option key={s.version} value={String(s.version)}>
                v{s.version}
                {s.version === latest?.version ? ' (latest)' : ''}
              </option>
            ))}
          </Select>
        </FormControl>
      </HStack>

      {loading ? <Text mb={4}>Loading…</Text> : null}

      {readOnly ? (
        <Text fontSize="sm" color="gray.600" mb={4}>
          Read-only view of version {selectedVersion}.
        </Text>
      ) : null}

      <Table size="sm" mb={4} sx={{ 'th, td': { verticalAlign: 'top' } }}>
        <Thead>
          <Tr>
            <Th>Metric</Th>
            <Th>Key</Th>
            <Th>
              <Tooltip
                hasArrow
                placement="top"
                label="Threshold behavior: Numeric (higher better) uses >= (inclusive). Numeric (lower better) uses < (exclusive)."
              >
                <HStack spacing={1} align="center" w="fit-content" cursor="help">
                  <Text as="span" textDecoration="underline" textDecorationStyle="dotted">
                    Kind
                  </Text>
                  <InfoOutlineIcon boxSize={3.5} color="blue.500" aria-label="Kind threshold behavior info" />
                </HStack>
              </Tooltip>
            </Th>
            <Th>Unit</Th>
            <Th isNumeric>Threshold 3</Th>
            <Th isNumeric>Threshold 2</Th>
            <Th>Score 3 label</Th>
            <Th>Score 2 label</Th>
            <Th>Score 1 label</Th>
            <Th w="50px" />
          </Tr>
        </Thead>
        <Tbody>
          {displayMetrics.map((row, index) => (
            <Tr key={`${row.key}-${index}`}>
              <Td>
                <Input
                  size="sm"
                  value={row.label}
                  isReadOnly={readOnly}
                  onChange={(e) => updateMetric(index, 'label', e.target.value)}
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={row.key}
                  isReadOnly={readOnly}
                  onChange={(e) => updateMetric(index, 'key', e.target.value)}
                />
              </Td>
              <Td>
                <Select
                  size="sm"
                  value={row.kind}
                  isDisabled={readOnly}
                  onChange={(e) => updateMetric(index, 'kind', e.target.value)}
                >
                  {KIND_OPTIONS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={row.unit || ''}
                  isReadOnly={readOnly}
                  onChange={(e) => updateMetric(index, 'unit', e.target.value)}
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  type="number"
                  value={row.thresholdScore3}
                  isReadOnly={readOnly}
                  onChange={(e) =>
                    updateMetric(index, 'thresholdScore3', e.target.value)
                  }
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  type="number"
                  value={row.thresholdScore2}
                  isReadOnly={readOnly}
                  onChange={(e) =>
                    updateMetric(index, 'thresholdScore2', e.target.value)
                  }
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={row.labelScore3 || ''}
                  isReadOnly={readOnly}
                  onChange={(e) =>
                    updateMetric(index, 'labelScore3', e.target.value)
                  }
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={row.labelScore2 || ''}
                  isReadOnly={readOnly}
                  onChange={(e) =>
                    updateMetric(index, 'labelScore2', e.target.value)
                  }
                />
              </Td>
              <Td>
                <Input
                  size="sm"
                  value={row.labelScore1 || ''}
                  isReadOnly={readOnly}
                  onChange={(e) =>
                    updateMetric(index, 'labelScore1', e.target.value)
                  }
                />
              </Td>
              <Td>
                {!readOnly ? (
                  <IconButton
                    size="sm"
                    aria-label="Delete metric"
                    icon={<DeleteIcon />}
                    variant="ghost"
                    onClick={() => deleteMetric(index)}
                  />
                ) : null}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!readOnly ? (
        <HStack spacing={3} mb={8}>
          <Button onClick={addMetric}>Add metric</Button>
        </HStack>
      ) : null}

      <Heading size="sm" mb={3} mt={2}>
        Final score (Grade A / B / C)
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Mean of all scored rows (each 1–3). Grade A if mean ≥ first threshold; Grade B if mean ≥ second
        threshold but below Grade A; otherwise Grade C. Defaults match the previous behavior (2.5 / 1.5).
      </Text>
      <HStack spacing={6} mb={6} flexWrap="wrap" align="flex-end">
        <FormControl maxW="220px">
          <FormLabel fontSize="sm">Min mean for Grade A</FormLabel>
          <Input
            size="sm"
            type="number"
            step="any"
            value={displayFinalScoreBands.minAverageForGradeA ?? ''}
            isReadOnly={readOnly}
            onChange={(e) =>
              updateFinalScoreBand('minAverageForGradeA', e.target.value)
            }
          />
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel fontSize="sm">Min mean for Grade B</FormLabel>
          <Input
            size="sm"
            type="number"
            step="any"
            value={displayFinalScoreBands.minAverageForGradeB ?? ''}
            isReadOnly={readOnly}
            onChange={(e) =>
              updateFinalScoreBand('minAverageForGradeB', e.target.value)
            }
          />
        </FormControl>
      </HStack>

      <Heading size="sm" mb={3} mt={2}>
        Grade Thresholds
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Maximum percentage values for each grade. Grade A = score 3, Grade B = score 2, Grade C = score 1.
      </Text>
      <Table size="sm" mb={4}>
        <Thead>
          <Tr>
            <Th>KPI</Th>
            <Th isNumeric>Grade A (≤ %)</Th>
            <Th isNumeric>Grade B (≤ %)</Th>
            <Th isNumeric>Grade C (≤ %)</Th>
          </Tr>
        </Thead>
        <Tbody>
          {[
            { key: 'exposureToRunRate', label: 'Exposure to Run Rate' },
            { key: 'exposureToRunOff', label: 'Exposure to Run Off' },
          ].map((row) => (
            <Tr key={row.key}>
              <Td fontWeight="medium">{row.label}</Td>
              {['grade3', 'grade2', 'grade1'].map((grade) => (
                <Td key={grade} isNumeric>
                  <Input
                    size="sm"
                    type="number"
                    textAlign="right"
                    maxW="80px"
                    value={displayGradeThresholds[row.key]?.[grade] ?? ''}
                    isReadOnly={readOnly}
                    onChange={(e) => updateGradeThreshold(row.key, grade, e.target.value)}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!readOnly ? (
        <HStack spacing={3}>
          <Button variant="brand" onClick={saveModal.onOpen}>
            Save
          </Button>
        </HStack>
      ) : null}

      <Modal isOpen={saveModal.isOpen} onClose={saveModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create new scorecard version?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Existing saved tearsheets will keep their previous scores. Only{' '}
              <strong>new</strong> tearsheet fetches will use this version.
            </Text>
            <FormLabel>Note (optional)</FormLabel>
            <Textarea
              value={saveNote}
              onChange={(e) => setSaveNote(e.target.value)}
              placeholder="Describe what changed"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={saveModal.onClose}>
              Cancel
            </Button>
            <Button variant="brand" isLoading={saving} onClick={confirmSave}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ContentBox>
  );
}

export default TearsheetScorecardConfigAdmin;
